import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SEOProps, generatePageTitle, generateCanonicalUrl } from "@/utils/seo";
import { SITE_NAME, SITE_KEYWORDS, SEO_CONFIG } from "@/constants/seo";

interface ExtendedSEOProps extends SEOProps {
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
}

const SEO = ({
  title,
  description = SEO_CONFIG.description,
  keywords = SITE_KEYWORDS,
  canonical,
  ogImage = SEO_CONFIG.openGraph.images[0].url,
  ogType = "website",
  noindex = false,
  structuredData,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
}: ExtendedSEOProps) => {
  const location = useLocation();
  const pageTitle = generatePageTitle(title);
  const canonicalUrl = canonical || generateCanonicalUrl(location.pathname);

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute("content", content);
    };

    const removeMetaTag = (name: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      const meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (meta) meta.remove();
    };

    // Basic meta tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords.join(", "));
    updateMetaTag("author", articleAuthor || "Ekselencia");
    updateMetaTag("language", "Spanish");
    updateMetaTag("revisit-after", "7 days");
    updateMetaTag("geo.region", "ES");
    updateMetaTag("geo.placename", "España");
    
    if (noindex) {
      updateMetaTag("robots", "noindex, nofollow");
      updateMetaTag("googlebot", "noindex, nofollow");
    } else {
      updateMetaTag("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
      updateMetaTag("googlebot", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
      updateMetaTag("bingbot", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
    }

    // Open Graph tags
    updateMetaTag("og:title", pageTitle, true);
    updateMetaTag("og:description", description, true);
    updateMetaTag("og:type", ogType, true);
    updateMetaTag("og:url", canonicalUrl, true);
    updateMetaTag("og:site_name", SITE_NAME, true);
    updateMetaTag("og:image", ogImage, true);
    updateMetaTag("og:image:width", "1200", true);
    updateMetaTag("og:image:height", "630", true);
    updateMetaTag("og:image:alt", pageTitle, true);
    updateMetaTag("og:locale", "es_ES", true);
    updateMetaTag("og:locale:alternate", "ca_ES", true);

    // Article-specific Open Graph tags
    if (ogType === "article") {
      if (articlePublishedTime) {
        updateMetaTag("article:published_time", articlePublishedTime, true);
      }
      if (articleModifiedTime) {
        updateMetaTag("article:modified_time", articleModifiedTime, true);
      }
      if (articleAuthor) {
        updateMetaTag("article:author", articleAuthor, true);
      }
      if (articleSection) {
        updateMetaTag("article:section", articleSection, true);
      }
    } else {
      // Clean up article tags if not an article
      removeMetaTag("article:published_time", true);
      removeMetaTag("article:modified_time", true);
      removeMetaTag("article:author", true);
      removeMetaTag("article:section", true);
    }

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", pageTitle);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", ogImage);
    updateMetaTag("twitter:image:alt", pageTitle);
    updateMetaTag("twitter:site", "@ekselencia");
    updateMetaTag("twitter:creator", "@ekselencia");

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);

    // Structured data
    if (structuredData) {
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      
      // Remove existing structured data scripts
      document.querySelectorAll('script[type="application/ld+json"].dynamic-schema').forEach(el => el.remove());
      
      // Add new structured data
      dataArray.forEach((data, index) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.className = "dynamic-schema";
        script.text = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      // Clean up dynamic structured data on unmount
      document.querySelectorAll('script[type="application/ld+json"].dynamic-schema').forEach(el => el.remove());
      // Clean up article-specific meta tags
      removeMetaTag("article:published_time", true);
      removeMetaTag("article:modified_time", true);
      removeMetaTag("article:author", true);
      removeMetaTag("article:section", true);
    };
  }, [pageTitle, description, keywords, canonicalUrl, ogImage, ogType, noindex, structuredData, articlePublishedTime, articleModifiedTime, articleAuthor, articleSection]);

  return null;
};

export default SEO;
